import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SearchResults, CountyResearchRecord } from "./types";
import { RecordDetailModal } from "./RecordDetailModal";
import { formatCurrency } from "@/components/investor/utils";
import { format } from "date-fns";
import { ExternalLink, Eye, RefreshCw, Trash2, Download, ChevronLeft, ChevronRight, ChevronDown, Save, X, Upload, Camera, Pencil } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import * as XLSX from 'xlsx';

interface ResearchResultsProps {
  searchResults: SearchResults;
  onRefresh: () => void;
  defaultCountyFilter?: string;
}

export function ResearchResults({ searchResults, defaultCountyFilter }: ResearchResultsProps) {
  const [selectedRecord, setSelectedRecord] = useState<CountyResearchRecord | null>(null);
  const [records, setRecords] = useState<CountyResearchRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [reviewIndex, setReviewIndex] = useState<number | null>(null);
  const [manualAddress, setManualAddress] = useState('');
  const [spreadsheetId, setSpreadsheetId] = useState("");
  const [isSavingToSheets, setIsSavingToSheets] = useState(false);
  const [countyFilter, setCountyFilter] = useState<string>(defaultCountyFilter || "all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [capturingRecordId, setCapturingRecordId] = useState<string | null>(null);
  const [uploadingRecordId, setUploadingRecordId] = useState<string | null>(null);
  const fileInputRef = useState<{ [key: string]: HTMLInputElement | null }>({})[0];
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [editingRecord, setEditingRecord] = useState<Partial<CountyResearchRecord> & { id: string } | null>(null);

  const allRecords = records.length > 0 ? records : searchResults.records;
  
  let displayRecords = allRecords;
  
  // Apply county filter
  if (countyFilter !== "all") {
    displayRecords = displayRecords.filter(r => r.county?.toLowerCase() === countyFilter.toLowerCase());
  }
  
  // Apply status filter
  if (statusFilter === "melissa") {
    displayRecords = displayRecords.filter(r => r.status === 'ready_for_melissa');
  } else if (statusFilter === "needs_review") {
    displayRecords = displayRecords.filter(r => r.address_needs_review);
  }
  
  const recordsNeedingReview = displayRecords.filter(r => r.address_needs_review);
  const comalRecordsNeedingReview = displayRecords.filter(r => r.county === 'comal' && !r.grantor_address);
  const recordsMissingAddress = displayRecords.filter(r => !r.grantor_address);
  const recordsReadyForMelissa = allRecords.filter(r => r.status === 'ready_for_melissa');
  const counties = Array.from(new Set(allRecords.map(r => r.county).filter(Boolean)));
  
  // Determine if we're in address-capture review mode (for counties like Warren where address_needs_review isn't set)
  const isAddressCaptureMode = reviewIndex !== null && !displayRecords[reviewIndex]?.address_needs_review && !displayRecords[reviewIndex]?.grantor_address;
  const isComalReviewMode = reviewIndex !== null && displayRecords[reviewIndex]?.county === 'comal';
  const activeReviewList = isComalReviewMode ? comalRecordsNeedingReview : isAddressCaptureMode ? recordsMissingAddress : recordsNeedingReview;
  
  // Build review record numbers based on the active review mode
  const reviewRecordNumbers = displayRecords
    .map((r, idx) => {
      if (isComalReviewMode) {
        return (r.county === 'comal' && !r.grantor_address) ? idx : -1;
      } else if (isAddressCaptureMode) {
        return (!r.grantor_address) ? idx : -1;
      } else {
        return r.address_needs_review ? idx : -1;
      }
    })
    .filter(idx => idx !== -1);
  const currentReviewPosition = reviewIndex !== null ? reviewRecordNumbers.indexOf(reviewIndex) + 1 : 0;

  const loadRecords = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('county_research')
        .select('*')
        .is('deleted_at', null) // Only load non-deleted records
        .order('filing_date', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;

      const records = (data || []) as CountyResearchRecord[];
      
      // Auto-fix: mark records with addresses as completed
      const needsStatusFix = records.filter(r => 
        r.grantor_address && r.grantor_address.trim() !== '' && r.status !== 'completed'
      );
      if (needsStatusFix.length > 0) {
        await Promise.all(needsStatusFix.map(r =>
          supabase.from('county_research').update({ status: 'completed' }).eq('id', r.id)
        ));
        // Update local data to reflect the fix
        records.forEach(r => {
          if (r.grantor_address && r.grantor_address.trim() !== '' && r.status !== 'completed') {
            r.status = 'completed';
          }
        });
      }
      
      setRecords(records);
    } catch (error) {
      console.error("Error loading records:", error);
      toast.error("Failed to load records");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, []);

  const handleDelete = async (recordId: string) => {
    if (!confirm("Are you sure you want to delete this record? It will stay deleted permanently.")) {
      return;
    }

    try {
      // Soft delete - set deleted_at timestamp instead of removing record
      const { error } = await supabase
        .from('county_research')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', recordId);

      if (error) throw error;

      toast.success("Record deleted successfully");
      setSelectedIds(new Set());
      loadRecords();
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error("Failed to delete record");
    }
  };

  const handleManualScreenshotUpload = async (record: CountyResearchRecord, file: File) => {
    setUploadingRecordId(record.id);
    toast.info("📤 Uploading screenshot and extracting seller (grantor) address...", { duration: 3000 });

    try {
      // Convert file to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const base64 = reader.result as string;
          resolve(base64.split(',')[1]); // Remove data:image/...;base64, prefix
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(file);

      const base64Image = await base64Promise;

      // Call edge function to extract LAND CONTRACT data (incl. seller/grantor address) from screenshot
      const { data, error } = await supabase.functions.invoke('extract-contract-screenshot', {
        body: {
          recordId: record.id,
          county: record.county,
          imageData: base64Image,
        },
      });

      if (error) throw error;

      const extractedGrantorAddress = data?.extractedData?.grantor_address as string | undefined;

      if (data?.success && extractedGrantorAddress) {
        toast.success(
          `✅ Seller (grantor) address extracted - Click Save & Next to confirm`,
          { duration: 4000 }
        );
        // Populate the manual address input (but do not auto-save)
        setManualAddress(extractedGrantorAddress);
      } else {
        toast.error("Couldn't find a seller (grantor) address in that screenshot");
      }
    } catch (error) {
      console.error("Error uploading screenshot:", error);
      toast.error("Failed to process screenshot");
    } finally {
      setUploadingRecordId(null);
    }
  };

  const handlePasteScreenshot = async (e: ClipboardEvent, record: CountyResearchRecord) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        e.preventDefault();
        const blob = items[i].getAsFile();
        if (blob) {
          await handleManualScreenshotUpload(record, blob);
        }
        break;
      }
    }
  };

  useEffect(() => {
    if (isReviewMode && reviewIndex !== null) {
      const handlePaste = (e: ClipboardEvent) => {
        handlePasteScreenshot(e, displayRecords[reviewIndex]);
      };
      
      document.addEventListener('paste', handlePaste);
      return () => document.removeEventListener('paste', handlePaste);
    }
  }, [isReviewMode, reviewIndex, displayRecords]);

  const handleCaptureAndExtract = async (record: CountyResearchRecord) => {
    if (!record.pdf_url) {
      toast.error("No document URL available for this record");
      return;
    }

    setCapturingRecordId(record.id);
    toast.info("📸 Capturing screenshot and extracting address...", { duration: 3000 });

    try {
      const { data, error } = await supabase.functions.invoke('capture-and-extract-address', {
        body: {
          recordId: record.id,
          documentUrl: record.pdf_url
        }
      });

      if (error) throw error;

      if (data.success) {
        toast.success(`✅ Address extracted: ${data.address}`, { duration: 5000 });
        loadRecords();
      } else {
        toast.error(data.error || "Failed to extract address");
      }
    } catch (error) {
      console.error("Error capturing and extracting:", error);
      toast.error("Failed to capture screenshot and extract address");
    } finally {
      setCapturingRecordId(null);
    }
  };

  const handleDeleteRecentImport = async () => {
    const recentRecords = displayRecords.filter(r => 
      r.document_number?.startsWith('UNKNOWN-')
    );

    if (recentRecords.length === 0) {
      toast.error("No recent import records found to delete");
      return;
    }

    if (!confirm(`Delete ${recentRecords.length} incorrectly imported records? This cannot be undone.`)) {
      return;
    }

    console.log('Deleting records:', recentRecords.map(r => ({ id: r.id, doc: r.document_number })));

    try {
      const idsToDelete = recentRecords.map(r => r.id);
      console.log(`Attempting to delete ${idsToDelete.length} records`);
      
      // Delete in very small batches to avoid URL length issues
      const batchSize = 5;
      let deletedCount = 0;
      let failedCount = 0;
      
      for (let i = 0; i < idsToDelete.length; i += batchSize) {
        const batch = idsToDelete.slice(i, i + batchSize);
        console.log(`Deleting batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(idsToDelete.length / batchSize)}`);
        
        const { error, count } = await supabase
          .from('county_research')
          .delete({ count: 'exact' })
          .in('id', batch);

        if (error) {
          console.error('Batch delete error:', error);
          failedCount += batch.length;
        } else {
          deletedCount += count || 0;
          console.log(`Batch deleted successfully: ${count} records`);
        }
      }
      
      if (failedCount > 0) {
        toast.error(`Deleted ${deletedCount} records, ${failedCount} failed`);
      } else {
        toast.success(`Successfully deleted ${deletedCount} records`);
      }

      setSelectedIds(new Set());
      await loadRecords();
    } catch (error) {
      console.error("Error deleting records:", error);
      toast.error(`Failed to delete records: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDeleteNonDickinson = async () => {
    const nonDickinsonRecords = displayRecords.filter(r => 
      r.county?.toLowerCase() !== 'dickinson'
    );

    if (nonDickinsonRecords.length === 0) {
      toast.error("No non-Dickinson County records found to delete");
      return;
    }

    if (!confirm(`Delete ${nonDickinsonRecords.length} non-Dickinson County records? This cannot be undone.`)) {
      return;
    }

    try {
      const idsToDelete = nonDickinsonRecords.map(r => r.id);
      
      const batchSize = 10;
      let deletedCount = 0;
      
      for (let i = 0; i < idsToDelete.length; i += batchSize) {
        const batch = idsToDelete.slice(i, i + batchSize);
        
        const { error, count } = await supabase
          .from('county_research')
          .delete({ count: 'exact' })
          .in('id', batch);

        if (error) {
          console.error('Batch delete error:', error);
        } else {
          deletedCount += count || 0;
        }
      }
      
      toast.success(`Successfully deleted ${deletedCount} non-Dickinson County records`);
      setSelectedIds(new Set());
      await loadRecords();
    } catch (error) {
      console.error("Error deleting records:", error);
      toast.error("Failed to delete records");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) {
      toast.error("Please select records to delete");
      return;
    }

    if (!confirm(`Are you sure you want to delete ${selectedIds.size} record(s)? They will stay deleted permanently.`)) {
      return;
    }

    try {
      // Soft delete - set deleted_at timestamp
      const { error } = await supabase
        .from('county_research')
        .update({ deleted_at: new Date().toISOString() })
        .in('id', Array.from(selectedIds));

      if (error) throw error;

      toast.success(`Successfully deleted ${selectedIds.size} record(s)`);
      setSelectedIds(new Set());
      await loadRecords();
    } catch (error) {
      console.error("Error deleting records:", error);
      toast.error("Failed to delete records");
    }
  };

  const handleDeleteAllRecords = async () => {
    const totalCount = allRecords.length;
    
    if (totalCount === 0) {
      toast.error("No records to delete");
      return;
    }

    if (!confirm(`⚠️ WARNING: This will permanently delete ALL ${totalCount} records from the county research database. This action CANNOT be undone. Type "DELETE ALL" to confirm.`)) {
      return;
    }

    const secondConfirm = prompt(`Type "DELETE ALL" to confirm deletion of ${totalCount} records:`);
    if (secondConfirm !== "DELETE ALL") {
      toast.error("Deletion cancelled - confirmation text did not match");
      return;
    }

    try {
      setIsLoading(true);
      
      // Delete all records in batches
      const batchSize = 100;
      const allIds = allRecords.map(r => r.id);
      let deletedCount = 0;
      
      for (let i = 0; i < allIds.length; i += batchSize) {
        const batch = allIds.slice(i, i + batchSize);
        
        const { error, count } = await supabase
          .from('county_research')
          .delete({ count: 'exact' })
          .in('id', batch);

        if (error) {
          console.error('Batch delete error:', error);
          throw error;
        } else {
          deletedCount += count || 0;
        }
      }
      
      toast.success(`Successfully deleted all ${deletedCount} records from county research`);
      setSelectedIds(new Set());
      await loadRecords();
    } catch (error) {
      console.error("Error deleting all records:", error);
      toast.error("Failed to delete all records");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === displayRecords.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayRecords.map(r => r.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkCountyUpdate = async (fromCounty: string, toCounty: string, addressPattern: string) => {
    const recordsToUpdate = allRecords.filter(r => 
      r.county === fromCounty && 
      (r.property_address?.includes(addressPattern) || r.grantee_address?.includes(addressPattern))
    );

    if (recordsToUpdate.length === 0) {
      toast.error(`No ${fromCounty} records found with "${addressPattern}" in address`);
      return;
    }

    if (!confirm(`Update ${recordsToUpdate.length} records from "${fromCounty}" to "${toCounty}"?`)) {
      return;
    }

    try {
      const updates = recordsToUpdate.map(record =>
        supabase
          .from('county_research')
          .update({ county: toCounty, updated_at: new Date().toISOString() })
          .eq('id', record.id)
      );

      await Promise.all(updates);
      toast.success(`Updated ${recordsToUpdate.length} records to ${toCounty}`);
      loadRecords();
    } catch (error: any) {
      console.error('Bulk county update error:', error);
      toast.error("Failed to update counties");
    }
  };

  const handleExportToExcel = () => {
    try {
      const exportData = displayRecords.map(record => ({
        'Document Number': record.document_number,
        'Filing Date': format(new Date(record.filing_date + 'T12:00:00'), 'MM/dd/yyyy'),
        'Seller (Grantor)': record.grantor_name,
        'Seller Address': record.grantor_address || record.grantee_address || '',
        'Buyer (Grantee)': record.grantee_name,
        'Buyer Address': record.grantee_address ? (record.grantor_address || '') : '',
        'Property Address': record.property_address || '',
        'Loan Amount': record.loan_amount || '',
        'Interest Rate': record.interest_rate ? (record.interest_rate * 100).toFixed(2) + '%' : '',
        'Monthly Payment': record.monthly_payment || '',
        'Term (Months)': record.term_months || '',
        'Balloon Amount': record.balloon_amount || '',
        'Balloon Due Date': record.balloon_due_date ? format(new Date(record.balloon_due_date), 'MM/dd/yyyy') : '',
        'Confidence Score': record.confidence_score || 0,
        'Status': record.is_active ? 'Active' : 'Inactive',
        'Address Needs Review': record.address_needs_review ? 'Yes' : 'No',
        'Notes': record.notes || ''
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'County Research');
      
      const fileName = `county-research-${format(new Date(), 'yyyy-MM-dd-HHmm')}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      toast.success(`Exported ${exportData.length} records to ${fileName}`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error('Failed to export to Excel');
    }
  };

  const handleSaveToGoogleSheets = async () => {
    if (!spreadsheetId.trim()) {
      toast.error("Spreadsheet ID required. Please enter your Google Spreadsheet ID");
      return;
    }

    if (displayRecords.length === 0) {
      toast.error("No data to save");
      return;
    }

    setIsSavingToSheets(true);

    try {
      const { data, error } = await supabase.functions.invoke('save-to-google-sheets', {
        body: {
          records: displayRecords,
          spreadsheetId: spreadsheetId.trim(),
          sheetName: 'County Research',
        },
      });

      if (error) throw error;

      toast.success(`Saved ${displayRecords.length} records to Google Sheets`);
    } catch (error: any) {
      console.error('Error saving to Google Sheets:', error);
      toast.error(error.message || "Failed to save to Google Sheets");
    } finally {
      setIsSavingToSheets(false);
    }
  };

  const startReviewMode = () => {
    // Clear any stale address state first
    setManualAddress('');
    const recordsNeedingReview = displayRecords.filter(r => r.address_needs_review);
    if (recordsNeedingReview.length > 0) {
      const firstIndex = displayRecords.findIndex(r => r.address_needs_review);
      setReviewIndex(firstIndex);
      setIsReviewMode(true);
    } else {
      toast.error("No records need address review");
    }
  };

  const closeReviewMode = () => {
    setIsReviewMode(false);
    setReviewIndex(null);
    setManualAddress('');
  };

  const handleSaveAndNext = async () => {
    if (reviewIndex === null) return;
    
    const currentRecord = displayRecords[reviewIndex];
    
    if (!manualAddress.trim()) {
      toast.error("Please enter an address");
      return;
    }

    try {
      // Find the next record BEFORE updating state
      let nextIndex = -1;
      if (currentRecord.county === 'comal') {
        nextIndex = displayRecords.findIndex((r, idx) => idx > reviewIndex && r.county === 'comal' && !r.grantor_address);
      } else if (!currentRecord.address_needs_review) {
        // Address-capture mode: find next record without address
        nextIndex = displayRecords.findIndex((r, idx) => idx > reviewIndex && !r.grantor_address);
      } else {
        nextIndex = displayRecords.findIndex((r, idx) => idx > reviewIndex && r.address_needs_review);
      }
      
      console.log('Saving address for record:', currentRecord.id, 'Address:', manualAddress);
      
      const { data, error } = await supabase
        .from('county_research')
        .update({
          grantor_address: manualAddress,
          address_needs_review: false,
          address_extraction_method: 'manual',
          address_confidence_score: 100,
          status: 'completed',
          updated_at: new Date().toISOString()
        })
        .eq('id', currentRecord.id)
        .select();

      if (error) {
        console.error('Database update error:', error);
        throw error;
      }
      
      console.log('Database update successful:', data);

      // Update local state immediately without reloading
      const updatedRecords = records.map(r => 
        r.id === currentRecord.id 
          ? {
              ...r,
              grantor_address: manualAddress,
              address_needs_review: false,
              address_extraction_method: 'manual',
              address_confidence_score: 100,
              status: 'completed'
            }
          : r
      );
      setRecords(updatedRecords);
      
      toast.success("Address saved!");
      
      // Clear input immediately to prevent it showing in next record
      setManualAddress('');
      
      // Move to next record AFTER clearing
      if (nextIndex !== -1) {
        setTimeout(() => {
          setReviewIndex(nextIndex);
          const nextRecord = updatedRecords[nextIndex];
          setManualAddress(nextRecord?.grantor_address || '');
        }, 50);
      } else {
        closeReviewMode();
        toast.success("Finished reviewing all records!");
      }
    } catch (error) {
      console.error("Error saving address:", error);
      toast.error("Failed to save address");
    }
  };

  const handleDeleteAndNext = async () => {
    if (reviewIndex === null) return;
    
    const currentRecord = displayRecords[reviewIndex];
    
    try {
      // Soft delete - set deleted_at timestamp
      const { error } = await supabase
        .from('county_research')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', currentRecord.id);

      if (error) throw error;

      toast.success("Record deleted");
      
      // Remove record from local state immediately (don't reload - that causes index drift)
      const updatedRecords = records.filter(r => r.id !== currentRecord.id);
      setRecords(updatedRecords);
      
      // Recompute displayRecords from updated list
      let updatedDisplay = updatedRecords;
      if (countyFilter !== "all") {
        updatedDisplay = updatedDisplay.filter(r => r.county?.toLowerCase() === countyFilter.toLowerCase());
      }
      if (statusFilter === "melissa") {
        updatedDisplay = updatedDisplay.filter(r => r.status === 'ready_for_melissa');
      } else if (statusFilter === "needs_review") {
        updatedDisplay = updatedDisplay.filter(r => r.address_needs_review);
      }
      
      // Check if current record was Comal to determine review mode type
      const isComalMode = currentRecord.county === 'comal';
      const isAddressMode = !currentRecord.address_needs_review;
      
      // Find the next record in the UPDATED list, starting from same position (since we removed one)
      const searchStart = Math.min(reviewIndex, updatedDisplay.length - 1);
      const nextIndex = updatedDisplay.findIndex((r, idx) => 
        idx >= searchStart && 
        (isComalMode ? (r.county === 'comal' && !r.grantor_address) : isAddressMode ? !r.grantor_address : r.address_needs_review)
      );
      
      setManualAddress('');
      
      if (nextIndex !== -1) {
        setReviewIndex(nextIndex);
        setManualAddress(updatedDisplay[nextIndex].grantor_address || '');
      } else {
        closeReviewMode();
        toast.success("Finished reviewing all records!");
      }
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error("Failed to delete record");
    }
  };

  const handlePrevious = () => {
    if (reviewIndex === null || reviewIndex === 0) return;
    
    // Check if current record is Comal to determine review mode type
    const isComalMode = displayRecords[reviewIndex]?.county === 'comal';
    const isAddressMode = !displayRecords[reviewIndex]?.address_needs_review;
    
    const prevIndex = displayRecords.slice(0, reviewIndex).reverse().findIndex(r => 
      isComalMode ? (r.county === 'comal' && !r.grantor_address) : isAddressMode ? !r.grantor_address : r.address_needs_review
    );
    
    if (prevIndex !== -1) {
      const actualIndex = reviewIndex - prevIndex - 1;
      setReviewIndex(actualIndex);
      setManualAddress(displayRecords[actualIndex].grantor_address || '');
    }
  };

  const handleNext = () => {
    if (reviewIndex === null) return;
    
    const isComalMode = displayRecords[reviewIndex]?.county === 'comal';
    const isAddressMode = !displayRecords[reviewIndex]?.address_needs_review;
    
    const nextIndex = displayRecords.findIndex((r, idx) => 
      idx > reviewIndex && (isComalMode ? (r.county === 'comal' && !r.grantor_address) : isAddressMode ? !r.grantor_address : r.address_needs_review)
    );
    
    if (nextIndex !== -1) {
      setReviewIndex(nextIndex);
      setManualAddress(displayRecords[nextIndex].grantor_address || '');
    }
  };

  const handleInlineEdit = (record: CountyResearchRecord) => {
    setEditingRecord({
      id: record.id,
      grantor_name: record.grantor_name || '',
      grantee_name: record.grantee_name || '',
      grantor_address: record.grantor_address || '',
      grantee_address: record.grantee_address || '',
      property_address: record.property_address || '',
      loan_amount: record.loan_amount || undefined,
      interest_rate: record.interest_rate || undefined,
      monthly_payment: record.monthly_payment || undefined,
      term_months: record.term_months || undefined,
      balloon_amount: record.balloon_amount || undefined,
      notes: record.notes || '',
    });
  };

  const handleSaveInlineEdit = async () => {
    if (!editingRecord) return;
    try {
      const { error } = await supabase
        .from('county_research')
        .update({
          grantor_name: editingRecord.grantor_name || null,
          grantee_name: editingRecord.grantee_name || null,
          grantor_address: editingRecord.grantor_address || null,
          grantee_address: editingRecord.grantee_address || null,
          property_address: editingRecord.property_address || null,
          loan_amount: editingRecord.loan_amount || null,
          interest_rate: editingRecord.interest_rate || null,
          monthly_payment: editingRecord.monthly_payment || null,
          term_months: editingRecord.term_months || null,
          balloon_amount: editingRecord.balloon_amount || null,
          notes: editingRecord.notes || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingRecord.id);

      if (error) throw error;
      toast.success("Record updated!");
      setEditingRecord(null);
      loadRecords();
    } catch (error) {
      console.error("Error updating record:", error);
      toast.error("Failed to update record");
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (!isReviewMode) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only trigger shortcuts if Ctrl is held AND the key matches
      // This prevents accidental triggers from paste operations
      if (!e.ctrlKey) return;
      
      if (e.key === 'Delete') {
        e.preventDefault();
        handleDeleteAndNext();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleSaveAndNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isReviewMode, reviewIndex, displayRecords, manualAddress]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground mb-2" />
          <p className="text-muted-foreground">Loading records...</p>
        </CardContent>
      </Card>
    );
  }

  if (displayRecords.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <p className="text-muted-foreground">No research records yet. Start capturing contracts from Dickinson County.</p>
        </CardContent>
      </Card>
    );
  }

  const ownerFinanceCount = displayRecords.filter(r => r.is_potential_owner_finance).length;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>All Extracted Records</CardTitle>
                <CardDescription>
                  {displayRecords.length} total records, {ownerFinanceCount} potential owner-financed
                  {selectedIds.size > 0 && ` • ${selectedIds.size} selected`}
                </CardDescription>
              </div>
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <Select value={countyFilter} onValueChange={setCountyFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by county" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Counties</SelectItem>
                  {counties.map(county => (
                    <SelectItem key={county} value={county}>
                      {county.charAt(0).toUpperCase() + county.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="melissa">Ready for Melissa Data ({recordsReadyForMelissa.length})</SelectItem>
                  <SelectItem value="needs_review">Needs Review ({recordsNeedingReview.length})</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Google Spreadsheet ID (paste from URL)"
                value={spreadsheetId}
                onChange={(e) => setSpreadsheetId(e.target.value)}
                className="w-80"
              />
              <Button
                variant="default"
                size="sm"
                onClick={startReviewMode}
                disabled={recordsNeedingReview.length === 0}
                className="bg-primary hover:bg-primary/90"
              >
                <Eye className="mr-1 h-4 w-4" />
                Review ({recordsNeedingReview.length})
              </Button>
              {recordsMissingAddress.length > 0 && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    setManualAddress('');
                    const firstIndex = displayRecords.findIndex(r => !r.grantor_address);
                    if (firstIndex !== -1) {
                      setReviewIndex(firstIndex);
                      setIsReviewMode(true);
                    }
                  }}
                >
                  <Camera className="mr-1 h-4 w-4" />
                  Review Addresses ({recordsMissingAddress.length})
                </Button>
              )}
              {countyFilter === 'comal' && comalRecordsNeedingReview.length > 0 && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {
                    setManualAddress('');
                    const firstIndex = displayRecords.findIndex(r => r.county === 'comal' && !r.grantor_address);
                    if (firstIndex !== -1) {
                      setReviewIndex(firstIndex);
                      setIsReviewMode(true);
                    }
                  }}
                >
                  Review Comal Addresses ({comalRecordsNeedingReview.length})
                </Button>
              )}
              {countyFilter === 'comal' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (!confirm('This will clear ALL addresses for Comal County records so you can re-enter them. Continue?')) {
                      return;
                    }
                    try {
                      // Close review mode first to prevent stale data
                      closeReviewMode();
                      
                      const { data, error } = await supabase.functions.invoke('clear-comal-addresses');
                      if (error) throw error;
                      toast.success(`Cleared addresses for ${data.recordsUpdated} records`);
                      
                      // Await loadRecords and clear address state
                      await loadRecords();
                      setManualAddress('');
                    } catch (error) {
                      console.error('Error clearing addresses:', error);
                      toast.error('Failed to clear addresses');
                    }
                  }}
                  className="text-orange-600 hover:text-orange-700"
                >
                  Clear All Comal Addresses
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportToExcel}
              >
                <Download className="mr-2 h-4 w-4" />
                Export to Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSaveToGoogleSheets}
                disabled={isSavingToSheets || !spreadsheetId.trim()}
                title={!spreadsheetId.trim() ? "Enter Spreadsheet ID first" : "Append records to Google Sheets"}
              >
                <Upload className="mr-2 h-4 w-4" />
                {isSavingToSheets ? "Saving..." : "Save to Google Sheets"}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteRecentImport}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Failed Import
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteNonDickinson}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Non-Dickinson
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteAllRecords}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete ALL Records
              </Button>
              {selectedIds.size > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Selected ({selectedIds.size})
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkCountyUpdate('randall', 'madison', 'London, Ohio')}
                className="text-blue-600 hover:text-blue-700"
              >
                Fix Madison Counties
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={loadRecords}
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]">
                  <Checkbox
                    checked={selectedIds.size === displayRecords.length && displayRecords.length > 0}
                    onCheckedChange={toggleSelectAll}
                  />
                </TableHead>
                <TableHead>Status</TableHead>
                <TableHead>County</TableHead>
                <TableHead>Filing Date</TableHead>
                <TableHead>Doc #</TableHead>
                <TableHead>Seller</TableHead>
                <TableHead>Buyer</TableHead>
                <TableHead>Property</TableHead>
                <TableHead>Loan Amount</TableHead>
                <TableHead>Confidence</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayRecords.map((record) => (
                <>
                  <TableRow 
                    key={record.id} 
                    className="cursor-pointer hover:bg-muted/50" 
                    onClick={() => {
                      const newExpanded = new Set(expandedRows);
                      if (newExpanded.has(record.id)) {
                        newExpanded.delete(record.id);
                      } else {
                        newExpanded.add(record.id);
                      }
                      setExpandedRows(newExpanded);
                    }}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()} className="w-12">
                      <div className="flex items-center gap-2">
                        {expandedRows.has(record.id) ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                        <Checkbox
                          checked={selectedIds.has(record.id)}
                          onCheckedChange={() => toggleSelect(record.id)}
                        />
                      </div>
                    </TableCell>
                  <TableCell>
                    {record.status === 'completed' ? (
                      <Badge className="bg-green-600 hover:bg-green-700">Completed</Badge>
                    ) : record.is_active ? (
                      <Badge variant="default">Active</Badge>
                    ) : (
                      <Badge variant="secondary">Inactive</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {record.county || 'Unknown'}
                    </Badge>
                  </TableCell>
                  <TableCell>{format(new Date(record.filing_date + 'T12:00:00'), 'MM/dd/yyyy')}</TableCell>
                  <TableCell className="font-mono text-sm">{record.document_number}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{record.grantor_name}</p>
                      {record.address_needs_review ? (
                        <Badge variant="outline" className="text-xs">Address needed</Badge>
                      ) : record.grantor_address ? (
                        <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                          {record.grantor_address}
                        </p>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell>{record.grantee_name}</TableCell>
                  <TableCell className="max-w-[200px] truncate">{record.property_address || 'N/A'}</TableCell>
                  <TableCell>{record.loan_amount ? formatCurrency(record.loan_amount) : 'N/A'}</TableCell>
                  <TableCell>
                    <Badge 
                      variant={
                        (record.confidence_score || 0) > 80 ? 'default' : 
                        (record.confidence_score || 0) > 60 ? 'secondary' : 
                        'outline'
                      }
                    >
                      {record.confidence_score || 0}%
                    </Badge>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-2">
                      {record.address_needs_review ? (
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => setSelectedRecord(record)}
                        >
                          Add Address
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedRecord(record)}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.accept = 'image/*';
                          input.onchange = (e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) {
                              handleManualScreenshotUpload(record, file);
                            }
                          };
                          input.click();
                        }}
                        disabled={uploadingRecordId === record.id}
                        title="Upload screenshot to extract seller's address"
                      >
                        {uploadingRecordId === record.id ? (
                          <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Upload className="h-4 w-4 mr-1" />
                            Upload
                          </>
                        )}
                      </Button>
                      {record.pdf_url && (
                        <>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleCaptureAndExtract(record)}
                            disabled={capturingRecordId === record.id}
                            title="Capture screenshot and extract address with AI"
                          >
                            {capturingRecordId === record.id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Camera className="h-4 w-4 mr-1" />
                                Auto
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => window.open(record.pdf_url, '_blank')}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(record.id)}
                        title="Delete record"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {expandedRows.has(record.id) && (
                  <TableRow key={`${record.id}-expanded`}>
                    <TableCell colSpan={11} className="bg-muted/30 p-4">
                      {editingRecord?.id === record.id ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs font-semibold text-muted-foreground">Seller (Grantor)</label>
                              <Input
                                value={editingRecord.grantor_name || ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, grantor_name: e.target.value })}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-muted-foreground">Buyer (Grantee)</label>
                              <Input
                                value={editingRecord.grantee_name || ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, grantee_name: e.target.value })}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-muted-foreground">Seller Address</label>
                              <Input
                                value={editingRecord.grantor_address || ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, grantor_address: e.target.value })}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-muted-foreground">Buyer Address</label>
                              <Input
                                value={editingRecord.grantee_address || ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, grantee_address: e.target.value })}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="text-xs font-semibold text-muted-foreground">Property Address</label>
                              <Input
                                value={editingRecord.property_address || ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, property_address: e.target.value })}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-muted-foreground">Loan Amount</label>
                              <Input
                                type="number"
                                value={editingRecord.loan_amount ?? ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, loan_amount: e.target.value ? parseFloat(e.target.value) : undefined })}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-muted-foreground">Interest Rate (decimal, e.g. 0.08)</label>
                              <Input
                                type="number"
                                step="0.001"
                                value={editingRecord.interest_rate ?? ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, interest_rate: e.target.value ? parseFloat(e.target.value) : undefined })}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-muted-foreground">Monthly Payment</label>
                              <Input
                                type="number"
                                step="0.01"
                                value={editingRecord.monthly_payment ?? ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, monthly_payment: e.target.value ? parseFloat(e.target.value) : undefined })}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-muted-foreground">Term (Months)</label>
                              <Input
                                type="number"
                                value={editingRecord.term_months ?? ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, term_months: e.target.value ? parseInt(e.target.value) : undefined })}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-muted-foreground">Balloon Amount</label>
                              <Input
                                type="number"
                                value={editingRecord.balloon_amount ?? ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, balloon_amount: e.target.value ? parseFloat(e.target.value) : undefined })}
                                className="h-8 text-sm"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="text-xs font-semibold text-muted-foreground">Notes</label>
                              <Textarea
                                value={editingRecord.notes || ''}
                                onChange={(e) => setEditingRecord({ ...editingRecord, notes: e.target.value })}
                                className="text-sm"
                                rows={2}
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={handleSaveInlineEdit}>
                              <Save className="h-4 w-4 mr-1" />
                              Save Changes
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingRecord(null)}>
                              <X className="h-4 w-4 mr-1" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex justify-end">
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleInlineEdit(record); }}>
                              <Pencil className="h-4 w-4 mr-1" />
                              Edit Record
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="font-semibold text-muted-foreground mb-1">Seller Address</p>
                              <p>{record.grantor_address || record.grantee_address || 'Not available'}</p>
                            </div>
                            <div>
                              <p className="font-semibold text-muted-foreground mb-1">Buyer Address</p>
                              <p>{record.grantee_address ? record.grantor_address : ''}{!record.grantee_address && !record.grantor_address ? 'Not available' : ''}</p>
                            </div>
                            <div>
                              <p className="font-semibold text-muted-foreground mb-1">Interest Rate</p>
                              <p>{record.interest_rate ? `${(record.interest_rate * 100).toFixed(2)}%` : 'N/A'}</p>
                            </div>
                            <div>
                              <p className="font-semibold text-muted-foreground mb-1">Monthly Payment</p>
                              <p>{record.monthly_payment ? formatCurrency(record.monthly_payment) : 'N/A'}</p>
                            </div>
                            <div>
                              <p className="font-semibold text-muted-foreground mb-1">Term</p>
                              <p>{record.term_months ? `${record.term_months} months` : 'N/A'}</p>
                            </div>
                            <div>
                              <p className="font-semibold text-muted-foreground mb-1">Balloon Payment</p>
                              <p>{record.balloon_amount ? formatCurrency(record.balloon_amount) : 'None'}</p>
                            </div>
                            {record.balloon_due_date && (
                              <div>
                                <p className="font-semibold text-muted-foreground mb-1">Balloon Due Date</p>
                                <p>{format(new Date(record.balloon_due_date), 'MM/dd/yyyy')}</p>
                              </div>
                            )}
                            {record.legal_description && (
                              <div className="col-span-2">
                                <p className="font-semibold text-muted-foreground mb-1">Legal Description</p>
                                <p className="text-xs">{record.legal_description}</p>
                              </div>
                            )}
                            {record.notes && (
                              <div className="col-span-2">
                                <p className="font-semibold text-muted-foreground mb-1">Notes</p>
                                <p className="text-xs">{record.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {selectedRecord && (
        <RecordDetailModal
          record={selectedRecord}
          open={selectedRecord !== null}
          onClose={() => setSelectedRecord(null)}
          onUpdate={loadRecords}
        />
      )}

      {/* Review Mode Dialog - Narrow vertical layout for split screen */}
      {isReviewMode && reviewIndex !== null && displayRecords[reviewIndex] && (
        <Dialog open={isReviewMode} onOpenChange={(open) => !open && closeReviewMode()}>
          <DialogContent className="max-w-md w-full h-[95vh] overflow-hidden flex flex-col p-4 gap-3">
            {/* Compact Header */}
            <div className="flex items-center justify-between gap-2 pb-2 border-b shrink-0">
              <span className="text-sm font-bold">
                {currentReviewPosition}/{activeReviewList.length}
              </span>
              <div className="flex-1 bg-secondary h-1.5 rounded-full overflow-hidden mx-2">
                <div 
                  className="bg-primary h-full transition-all duration-300"
                  style={{ width: `${(currentReviewPosition / activeReviewList.length) * 100}%` }}
                />
              </div>
              <div className="flex gap-1">
                <Button
                  onClick={handlePrevious}
                  disabled={currentReviewPosition === 1}
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={currentReviewPosition >= activeReviewList.length}
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto space-y-3">
              {/* Record Info - Compact */}
              <div className="grid grid-cols-2 gap-2 p-2 bg-muted rounded text-xs">
                <div>
                  <label className="text-muted-foreground">Doc #</label>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(displayRecords[reviewIndex].document_number);
                      toast.success("Copied!");
                    }}
                    className="font-mono hover:bg-accent px-1 rounded cursor-pointer block truncate"
                    title="Click to copy"
                  >
                    {displayRecords[reviewIndex].document_number}
                  </button>
                </div>
                <div>
                  <label className="text-muted-foreground">Date</label>
                  <p>{format(new Date(displayRecords[reviewIndex].filing_date + 'T12:00:00'), 'MM/dd/yy')}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-muted-foreground">Grantor</label>
                  <p className="font-medium truncate">{displayRecords[reviewIndex].grantor_name}</p>
                </div>
                <div className="col-span-2">
                  <label className="text-muted-foreground">Grantee</label>
                  <p className="truncate">{displayRecords[reviewIndex].grantee_name}</p>
                </div>
                {displayRecords[reviewIndex].property_address && (
                  <div className="col-span-2">
                    <label className="text-muted-foreground">Property</label>
                    <p className="truncate">{displayRecords[reviewIndex].property_address}</p>
                  </div>
                )}
              </div>

              {/* Warning Box - context-aware terminology */}
              {displayRecords[reviewIndex]?.county === 'whatcom' ? (
                <div className="text-xs bg-red-50 dark:bg-red-950/20 border border-red-300 dark:border-red-800 p-2 rounded">
                  <p className="font-bold text-red-700 dark:text-red-400">⚠️ Find BENEFICIARY section!</p>
                  <p className="text-red-600 dark:text-red-300 mt-1">BENEFICIARY = Seller's address (Deed of Trust)</p>
                </div>
              ) : (
                <div className="text-xs bg-blue-50 dark:bg-blue-950/20 border border-blue-300 dark:border-blue-800 p-2 rounded">
                  <p className="font-bold text-blue-700 dark:text-blue-400">📋 Find SELLER/VENDOR mailing address</p>
                  <p className="text-blue-600 dark:text-blue-300 mt-1">Paste a screenshot of the contract to extract the seller's address</p>
                </div>
              )}

              {/* Address Input */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold">Seller Address</label>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file && reviewIndex !== null) {
                          handleManualScreenshotUpload(displayRecords[reviewIndex], file);
                        }
                      };
                      input.click();
                    }}
                    disabled={uploadingRecordId !== null}
                  >
                    {uploadingRecordId !== null ? (
                      <RefreshCw className="h-3 w-3 animate-spin" />
                    ) : (
                      <Upload className="h-3 w-3 mr-1" />
                    )}
                    Upload
                  </Button>
                </div>
                
                <Textarea
                  placeholder="Enter address or paste screenshot (Ctrl+V)"
                  value={manualAddress}
                  onChange={(e) => setManualAddress(e.target.value)}
                  rows={4}
                  className="text-sm"
                  autoFocus
                />

                {displayRecords[reviewIndex].grantor_address && (
                  <div className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950 p-2 rounded">
                    <strong>Saved:</strong> {displayRecords[reviewIndex].grantor_address}
                  </div>
                )}
              </div>

              {/* Document Link */}
              {displayRecords[reviewIndex].pdf_url && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-xs h-8"
                  onClick={() => window.open(displayRecords[reviewIndex].pdf_url, '_blank')}
                >
                  <ExternalLink className="h-3 w-3 mr-1" />
                  Open Document
                </Button>
              )}
            </div>

            {/* Action Buttons - Fixed at bottom */}
            <div className="flex gap-2 pt-2 border-t shrink-0">
              <Button
                onClick={handleDeleteAndNext}
                variant="destructive"
                size="sm"
                className="flex-1 h-9"
              >
                <X className="mr-1 h-4 w-4" />
                Delete
              </Button>
              <Button
                onClick={handleSaveAndNext}
                variant="default"
                size="sm"
                className="flex-1 h-9"
              >
                <Save className="mr-1 h-4 w-4" />
                Save
              </Button>
            </div>

            {/* Keyboard hints */}
            <div className="text-[10px] text-muted-foreground flex gap-3 justify-center shrink-0">
              <span>Ctrl+Enter: Save</span>
              <span>Ctrl+Del: Delete</span>
              <span>Ctrl+←/→: Nav</span>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
